import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { dashAPI } from "../api";
import { toast } from "react-toastify";

const BaseRoute = "organizationapi/api/v1/services";

// --- ASYNC THUNKS ---

export const fetchServices = createAsyncThunk(
  "services/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashAPI.get(BaseRoute);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchService = createAsyncThunk(
  "services/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const response = await dashAPI.get(`${BaseRoute}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const createService = createAsyncThunk(
  "services/create",
  async (values, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      // Root level properties
      if (values.title) formData.append("title", values.title);
      if (values.description)
        formData.append("description", values.description);

      // Map files to the new ServicesApplicationMediaBatchForm schema
      if (values.files && values.files.length > 0) {
        let fileIndex = 0;
        values.files.forEach((file) => {
          if (file instanceof File) {
            formData.append(`media.items[${fileIndex}].file`, file);
            // Append stringified meta object as required by the schema
            // formData.append(
            //   `media.items[${fileIndex}].meta`,
            //   JSON.stringify({}),
            // );
            fileIndex++;
          }
        });
      }

      const response = await dashAPI.post(BaseRoute, formData);
      toast.success("Service created successfully!");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const updateService = createAsyncThunk(
  "services/update",
  async ({ id, values }, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      // Root level properties
      if (values.title) formData.append("title", values.title);
      if (values.description)
        formData.append("description", values.description);

      if (values.files && values.files.length > 0) {
        let fileIndex = 0;
        values.files.forEach((file) => {
          if (file instanceof File) {
            formData.append(`media.items[${fileIndex}].file`, file);
            formData.append(
              `media.items[${fileIndex}].meta`,
              JSON.stringify({}),
            );
            fileIndex++;
          }
        });
      }

      const response = await dashAPI.patch(`${BaseRoute}/${id}`, formData);
      toast.success("Service updated successfully!");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const deleteService = createAsyncThunk(
  "services/delete",
  async (id, { rejectWithValue }) => {
    try {
      await dashAPI.delete(`${BaseRoute}/${id}`);
      toast.success("Service deleted successfully!");
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// --- SLICE & REDUCER ---
const servicesSlice = createSlice({
  name: "services",
  initialState: {
    loading: false,
    services: [],
    service: {},
    error: "",
  },
  reducers: {
    clearCurrentService: (state) => {
      state.service = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.loading = false;
        state.services = action.payload;
      })
      .addCase(fetchService.fulfilled, (state, action) => {
        state.loading = false;
        state.service = action.payload;
      })
      .addCase(createService.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.services.unshift(action.payload);
        }
      })
      .addCase(updateService.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.services.findIndex(
          (s) => s.id === action.payload.id,
        );
        if (index !== -1) {
          state.services[index] = action.payload;
        }
        if (state.service.id === action.payload.id) {
          state.service = action.payload;
        }
      })
      .addCase(deleteService.fulfilled, (state, action) => {
        state.loading = false;
        state.services = state.services.filter((s) => s.id !== action.payload);
      })
      .addMatcher(
        (action) =>
          action.type.startsWith("services/") &&
          action.type.endsWith("/pending"),
        (state) => {
          state.loading = true;
          state.error = "";
        },
      )
      .addMatcher(
        (action) =>
          action.type.startsWith("services/") &&
          action.type.endsWith("/rejected"),
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
          toast.error(
            action.payload || "An error occurred with the services API",
          );
        },
      );
  },
});

export const { clearCurrentService } = servicesSlice.actions;
export const ServicesReducer = servicesSlice.reducer;
